import React, { useEffect, useMemo, useState } from 'react';
import {
  CompensationRecord,
  fetchRecordAssessment,
  RecordAssessmentResponse,
} from '../../../../services/adminService';
import { fetchGradingTree } from '../../../../services/gradingService';
import type {
  GradingFamily,
  GradingRole,
  GradingSpecialization,
  GradingRoleCluster,
} from '../grading/types';
import { formatGradingScore, resolveRolePath, normalizeGradingTreePayload } from '../grading/utils';
import { buildRoleLabel } from './gradingEngine';
import { AdminModalTw, btnSecondary } from '../adminUi';

type Props = {
  record: CompensationRecord;
  onClose: () => void;
};

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
      <dt className="w-40 shrink-0 text-sm font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">{value || '—'}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
      {children}
    </section>
  );
}

export default function RecordGradingViewModal({ record, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RecordAssessmentResponse | null>(null);
  const [families, setFamilies] = useState<GradingFamily[]>([]);
  const [roleClusters, setRoleClusters] = useState<GradingRoleCluster[]>([]);
  const [specializations, setSpecializations] = useState<GradingSpecialization[]>([]);
  const [roles, setRoles] = useState<GradingRole[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [assessmentRes, treeRaw] = await Promise.all([
          fetchRecordAssessment(record.id),
          fetchGradingTree(),
        ]);
        if (cancelled) return;
        const tree = normalizeGradingTreePayload(treeRaw);
        setData(assessmentRes);
        setFamilies(tree.families);
        setRoleClusters(tree.roleClusters);
        setSpecializations(tree.specializations);
        setRoles(tree.roles);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Не удалось загрузить анкету');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [record.id]);

  const roleFallback = useMemo(() => {
    if (!record.role_id || !roles.length) return null;
    return roles.find((r) => r.id === record.role_id) ?? null;
  }, [record.role_id, roles]);

  const rolePath = useMemo(() => {
    if (!roleFallback) return null;
    return resolveRolePath(roleFallback, specializations, roleClusters, families);
  }, [roleFallback, specializations, roleClusters, families]);

  const employeeLabel = [record.last_name, record.first_name, record.middle_name]
    .filter(Boolean)
    .join(' ')
    || record.company_role_name;

  const assessment = data?.assessment;
  const modeLabel = assessment?.algorithm_version === 'full' ? 'Обычный' : 'Быстрый';

  return (
    <AdminModalTw
      title="Анкета грейдирования"
      onClose={onClose}
      wide
      footer={(
        <button type="button" className={btnSecondary} onClick={onClose}>
          Закрыть
        </button>
      )}
    >
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
        <p className="font-medium text-slate-900">{employeeLabel}</p>
        <p className="mt-1 text-slate-600">
          {record.company_name} · {record.company_role_name}
        </p>
      </div>

      {loading && <p className="text-sm text-slate-500">Загрузка анкеты…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && assessment && (
        <div className="space-y-4">
          <Section title="Общее">
            <dl className="space-y-2">
              <FieldRow label="Режим" value={modeLabel} />
              <FieldRow
                label="Дата"
                value={new Date(assessment.created_at).toLocaleString('ru-RU')}
              />
              <FieldRow label="Family" value={assessment.family_name} />
              <FieldRow label="Role cluster" value={assessment.role_cluster_name} />
              <FieldRow
                label="Specialization"
                value={assessment.specialization_name ?? 'Без specialization'}
              />
            </dl>
          </Section>

          {data!.calibrationAnswers.length > 0 && (
            <Section title="Калибровка">
              <div className="space-y-4">
                {data!.calibrationAnswers.map((item) => (
                  <div key={item.calibration_question_id} className="space-y-1">
                    <p className="text-sm font-medium text-slate-900">{item.question_text}</p>
                    <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                      {item.answer_text}
                    </p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {data!.scoringAnswers.length > 0 && (
            <Section title="Дифференцирующие факторы">
              <div className="space-y-4">
                {data!.scoringAnswers.map((item) => (
                  <div key={item.question_id} className="dc-grading-preview-question">
                    {item.factor_name && (
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                        {item.factor_name}
                      </p>
                    )}
                    <p className="font-medium text-slate-900">{item.question_text}</p>
                    <p className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                      {item.answer_text}
                      <span className="ml-2 text-emerald-700">({formatGradingScore(item.answer_score)} б.)</span>
                    </p>
                  </div>
                ))}
              </div>
              {assessment.total_score != null && (
                <p className="mt-3 text-sm text-slate-700">
                  Сумма баллов: <strong>{formatGradingScore(assessment.total_score)}</strong>
                </p>
              )}
            </Section>
          )}

          <Section title="Итог">
            <dl className="space-y-2">
              <FieldRow
                label="Роль"
                value={
                  assessment.computed_role_name
                    ? `${assessment.computed_role_name} · G${assessment.computed_grade_level ?? '—'}`
                    : '—'
                }
              />
              {assessment.total_score != null && (
                <FieldRow label="Сумма баллов" value={formatGradingScore(assessment.total_score)} />
              )}
            </dl>
          </Section>
        </div>
      )}

      {!loading && !error && !assessment && roleFallback && rolePath && (
        <div className="space-y-4">
          <p className="text-sm text-amber-800">
            Сохранённая анкета не найдена. Показаны данные по назначенной роли.
          </p>
          <Section title="Назначенная роль">
            <dl className="space-y-2">
              <FieldRow label="Family" value={rolePath.family?.name} />
              <FieldRow label="Role cluster" value={rolePath.roleCluster?.name} />
              <FieldRow
                label="Specialization"
                value={rolePath.spec?.name ?? 'Без specialization'}
              />
              <FieldRow
                label="Роль"
                value={buildRoleLabel(roleFallback, specializations, roleClusters, families)}
              />
            </dl>
          </Section>
        </div>
      )}

      {!loading && !error && !assessment && !roleFallback && (
        <p className="text-sm text-slate-600">
          Анкета грейдирования ещё не заполнялась. Назначьте грейд через «Назначить грейд».
        </p>
      )}
    </AdminModalTw>
  );
}
